import { Select, Spin, Typography } from 'antd';
import {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { Dayjs } from 'dayjs';
import { AlarmCode } from 'types/metconsole.types';
import api, { useGetHistoricalRangeIOPointsQuery } from 'services/metconsole';
import MainAppContext from 'contexts/MainAppContext';
import RealTimeContext from 'contexts/RealTimeContext';
import TimeSeriesContext from 'contexts/TimeSeriesContext';
import AlarmsIOPoint from 'configs/iopoints';
import styles from './index.module.scss';
import { utcDayjs } from '../../../../../../../../../../utils';

const dummyContent = [
  '05A              050 14',
  '05D              210 18',
  '23A',
  '23D WSA +20K 1MF',
  '1415 05A WSA +15K 2MD 050 10',
  '1415 23D WSA -15K 2MF',
  '1415 23',
  '1415 05D TURB MOD',
  '05A MBA -35K 3MA',
  '05',
  '23A MBA -30K RWY',
  '23',
  '0050 05A              100 3',
  '0050 23D     -10K 2MF',
  '0050 23A TURB SEV',
  '0050 05D',
];

const { Option } = Select;

const {
  wrapper, MBA, WSA, TURB, selector, list,
} = styles;

// eslint-disable-next-line no-underscore-dangle
const runwaysList = ((window as any)._env_.REACT_APP_RUNWAYS_IN_USE as string || '').split(',');

const {
  service, station, group, variable,
} = AlarmsIOPoint;

function AlarmsWindow() {
  const { realTimeMode } = useContext(MainAppContext);
  const { realTime } = useContext(RealTimeContext);
  const { range, currentPosition } = useContext(TimeSeriesContext);
  const rangeWithOffset = [range?.[0]?.clone().subtract(3, 'hour').toISOString(), range?.[1]?.clone().add(3, 'hour').toISOString()];
  const { data, isLoading } = useGetHistoricalRangeIOPointsQuery({
    route: {
      variable, group, service, station,
    },
    range: rangeWithOffset as string[],
  }, { refetchOnMountOrArgChange: true });
  const [updateIOPoint, updateIOPointResult] = api.endpoints.getIOPoint.useLazyQuery();

  const [filteredList, setFilteredList] = useState<string[] | undefined>([]);
  const [selectorValue, setSelectorValue] = useState<string[]>([]);

  const getClassName = (line: string) => {
    let className = '';

    const alarmCodeToClassName: Record<AlarmCode, string> = {
      [AlarmCode.MBA]: MBA,
      [AlarmCode.WSA]: WSA,
      [AlarmCode.TURB]: TURB,
    };

    const alarmCodes = Object.keys(alarmCodeToClassName) as AlarmCode[];

    alarmCodes.forEach((alarmCode) => {
      if (line.includes(alarmCode)) {
        className = alarmCodeToClassName[alarmCode];
      }
    });

    return className;
  };

  const calculateAllowedText = (numbersArray: string[], choosenRunways: string[]): string => {
    const allowedText = numbersArray.filter((number) => choosenRunways.includes(number)).map((number) => `${number}[AD]`).join('|');
    return allowedText;
  };

  const filterLinesByNumberAndText = ({
    runways,
    forbiddenText,
    textArray,
  }:{
    runways: string[],
    forbiddenText: string,
    textArray: string[],
  }): string[] => {
    const regexForbiddenText = new RegExp(forbiddenText);
    const filteredLines = (Array.isArray(textArray)
      ? textArray : [textArray]).filter((line) => regexForbiddenText.test(line));
    return filteredLines;
  };

  const filterLines = useCallback((lines: string[], runways: string[]) => {
    const forbiddenText: string = calculateAllowedText(runwaysList, runways);
    return filterLinesByNumberAndText({
      runways,
      forbiddenText,
      textArray: lines,
    });
  }, []);

  const getClosestPreviousDate = useCallback((
    targetDate: Dayjs,
    data: Record<string, string[]>,
  ) => {
    const parsedDates = Object.keys(data).map((d) => utcDayjs(d));

    const previousDates = parsedDates.filter((date) => date.isBefore(targetDate));

    if (previousDates.length) {
      return previousDates
        .reduce((prev, curr) => (curr.isAfter(prev) ? curr : prev));
    }

    return undefined;
  }, []);

  const findDataByClosestDate = useCallback((
    targetDate: Dayjs,
    data: Record<string, string[]>,
  ) => {
    const date = getClosestPreviousDate(targetDate, data);
    if (date) {
      return data[date.toISOString()];
    }
    return undefined;
  }, [getClosestPreviousDate]);

  const handleOnSelectorChange = (value: string[]) => {
    if (data) {
      const selectedDataByDate = findDataByClosestDate(
        utcDayjs(currentPosition) || utcDayjs(),
        data as Record<string, string[]>,
      );
      setSelectorValue(value);

      if (value.length) {
        setFilteredList(filterLines(selectedDataByDate || [], value));
      } else {
        setFilteredList(selectedDataByDate || []);
      }
    }
  };

  useEffect(() => {
    if (currentPosition && data) {
      const selectedDataByDate = findDataByClosestDate(
        utcDayjs(currentPosition),
      data as Record<string, string[]>,
      );
      setFilteredList(selectedDataByDate);

      if (selectorValue.length && selectedDataByDate) {
        setFilteredList(filterLines(selectedDataByDate, selectorValue));
      } else {
        setFilteredList(selectedDataByDate);
      }
    }
  }, [currentPosition, data, filterLines, findDataByClosestDate, selectorValue]);

  useEffect(() => {
    if (realTimeMode && realTime) {
      // TODO Disabled until it works!
      /*    updateIOPoint({
        variable, group, service, station,
      }); */
    }
  }, [realTime, realTimeMode, updateIOPoint]);

  useEffect(() => {
    const updatedData = updateIOPointResult.data as string[];
    if (updatedData) {
      // TODO: it should be updatedData
      const dummyData = [utcDayjs().toISOString()].concat(dummyContent);

      if (selectorValue.length) {
        setFilteredList(filterLines(dummyData, selectorValue));
      } else {
        setFilteredList(dummyData);
      }
    }
  }, [filterLines, realTime, selectorValue, updateIOPoint, updateIOPointResult]);

  return (
    <div className={wrapper}>
      <div className={selector}>
        <small>Filter</small>
        <Select
          style={{ width: '100%' }}
          onChange={handleOnSelectorChange}
          defaultValue={[]}
          mode="multiple"
          value={selectorValue}
        >
          {runwaysList.map((runway) => <Option key={runway} value={runway}>{`RWY${runway} in use`}</Option>)}
        </Select>
      </div>
      <Spin spinning={isLoading}>
        <small style={{ position: 'absolute', bottom: '0px', right: '0px' }}>{currentPosition && data && getClosestPreviousDate(utcDayjs(currentPosition), data as Record<string, string[]>) ? (getClosestPreviousDate(utcDayjs(currentPosition), data as Record<string, string[]>) as Dayjs).toISOString() : 'No data'}</small>
        <div className={list}>
          {(Array.isArray(filteredList) ? filteredList : [filteredList])?.map((line) => (
            <Typography.Paragraph key={line}>
              <span className={getClassName(line as string)}>{line}</span>
            </Typography.Paragraph>
          ))}
        </div>
      </Spin>
    </div>
  );
}

export default AlarmsWindow;
